import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import { tokenStore } from '@/lib/auth';
import { api } from '@/lib/api';
import { useToast } from '@/hooks/useToast';
import type { User } from '@/types/api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const WARNING_LEAD_MS = 60_000; // warn 60 seconds before expiry

interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

/** Decodes a JWT payload client-side without verifying the signature.
 *  We're only reading the exp claim so we know when to warn the user.
 *  Returns null on any parse error. */
function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // base64url -> base64
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '=='.slice(0, (4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Schedules a "Your session is about to expire" toast 60 seconds before the
 * current access token expires. The toast's action button silently calls
 * /auth/refresh to extend the session.
 */
export function useSessionTimeoutWarning(): void {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setSession = useAuthStore((s) => s.setSession);
  const setUser = useAuthStore((s) => s.setUser);
  const clear = useAuthStore((s) => s.clear);
  const toast = useToast();
  // Keep stable refs so the effect doesn't re-fire whenever the toast helpers
  // are recreated (they're new objects on every render).
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const setSessionRef = useRef(setSession);
  setSessionRef.current = setSession;
  const setUserRef = useRef(setUser);
  setUserRef.current = setUser;
  const clearRef = useRef(clear);
  clearRef.current = clear;

  useEffect(() => {
    if (!accessToken) return;
    const payload = decodeJwt(accessToken);
    if (!payload?.exp) return;

    const expiresAtMs = payload.exp * 1000;
    const warnAtMs = expiresAtMs - WARNING_LEAD_MS;
    const now = Date.now();
    const delay = warnAtMs - now;

    // If the token already expired or is about to within the warning window,
    // skip — the axios 401 interceptor will pick it up on the next request.
    if (delay <= 0) return;

    const timer = window.setTimeout(() => {
      toastRef.current.warning('Your session expires in 1 minute', {
        body: 'Stay signed in to continue working without losing changes.',
        durationMs: WARNING_LEAD_MS,
        action: {
          label: 'Stay signed in',
          onClick: async () => {
            try {
              const refresh = tokenStore.getRefresh();
              if (!refresh) throw new Error('No refresh token');
              // Use raw axios (not `api`) to avoid the response interceptor
              // triggering a second /auth/refresh which would revoke the new
              // token and immediately log the user out.
              const r = await axios.post<{
                access_token: string;
                refresh_token: string;
                user: User | null;
              }>(`${BASE_URL}/auth/refresh`, { refresh_token: refresh }, {
                headers: { 'Content-Type': 'application/json' },
                withCredentials: true,
              });

              const { access_token, refresh_token, user } = r.data;

              // Always update the stored tokens
              tokenStore.setAccess(access_token);
              if (refresh_token) tokenStore.setRefresh(refresh_token);

              if (user) {
                // Full response — update the whole session
                setSessionRef.current({ access_token, refresh_token, user });
              } else {
                // Backend didn't return a user body (unlikely but defensive):
                // fetch the profile separately using the new access token so
                // we don't accidentally clear the user from the store.
                try {
                  const me = await api.get<User>('/users/me');
                  setSessionRef.current({ access_token, refresh_token, user: me.data });
                } catch {
                  // Profile fetch failed but tokens are updated — just patch the
                  // access token in the store without clearing the user.
                  setUserRef.current(null);
                }
              }

              toastRef.current.success('Session extended');
            } catch {
              toastRef.current.error('Could not extend session', 'Please sign in again.');
              clearRef.current();
            }
          },
        },
      });
    }, delay);

    return () => window.clearTimeout(timer);
  }, [accessToken]);
}
